/* eslint-disable no-self-assign */
import { HttpClient } from '@angular/common/http';
import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  Inject,
  ViewEncapsulation,
  DoCheck,
} from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IEnvironment } from '../../../models/environment.model';
import { updateUrlWithApiGatewayBaseUrl } from '../../../../utils/utils/data-fetch-widget-utils';
import { TranslateService } from '@ngx-translate/core';
import { alphaNanoIdUtil } from '../../../../utils/utils/alpha-nano-id.util';
import { getValueFromNestedObject } from '../../../../utils/utils/nested-object-extractor.util';
import {
  IIntegrationFetchSyncRequest,
  IIntegrationFetchSyncRequestResponse,
} from '../../../models/integration-fetch-sync.model';

interface TableColumn {
  label: string;
  key: string;
  sortable?: boolean;
  width?: string; // CSS width value (e.g., '50px', 'fit-content', '10%')
  nowrap?: boolean;
}

type DataType = 'STATIC' | 'DYNAMIC';

@Component({
  selector: 'irembogov-formly-table-wrapper',
  templateUrl: './table-wrapper.component.html',
  styleUrls: ['./table-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FormlyTableWrapperComponent
  extends FieldType
  implements OnInit, OnDestroy, DoCheck
{
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private readonly dataFetchFields: string[] = ['CUSTOM_DROPDOWN_DATAFETCH'];

  loading = false;
  tableData: any[] = [];
  totalItems = 0;
  currentPage = 1;
  itemsPerPage = 10;
  sortOptions: any[] = [];
  selectedSort: any;
  columns: TableColumn[] = [];

  // Selection properties
  selectedRows: Set<number> = new Set();
  selectedItems: any[] = [];
  isSelectable = true;
  maxSelectable = 1;
  selectionOutputKey = '';
  selectionOutputPath = '';

  // Data type properties
  dataType: DataType = 'STATIC';
  staticRows: any[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    @Inject('environment') private readonly environment: IEnvironment
  ) {
    super();
  }

  ngOnInit() {
    this.initializeComponent();
    this.setupSearchSubscription();
    this.loadData();
  }

  private previousRowsRef: any[] = [];

  ngDoCheck() {
    const newRows = this.field.props?.rows ?? [];

    // Detect deep array changes
    if (!Array.isArray(newRows)) return;

    if (newRows !== this.previousRowsRef) {
      this.loading = true;
      this.previousRowsRef = newRows;
      this.staticRows = newRows;
    }
    this.loadStaticData();
  }

  private initializeComponent() {
    this.columns = this.field.props?.['columns'] || [];
    const dataset = this.field.props?.['dataset'];
    if (dataset?.['params']) {
      this.itemsPerPage = dataset['params']['size'] || 10;
      this.currentPage = (dataset['params']['page'] || 0) + 1;
    }
    this.sortOptions = this.columns
      .filter(col => col.sortable)
      .map(col => ({
        label: col.label,
        key: col.key,
      }));

    this.isSelectable = this.field.props?.['isSelectable'] || false;
    this.maxSelectable = this.field.props?.['maxSelectable'] || Infinity;
    this.selectionOutputKey =
      this.field.props?.['selectionOutputKey'] || this.field.key;
    this.selectionOutputPath = this.field.props?.['selectionOutputPath'] || '';

    // Initialize data type and static rows
    this.dataType = this.field.props?.['dataType'] || 'STATIC';
    this.staticRows =
      this.field.props && Array.isArray(this.field.props['rows'])
        ? this.field.props['rows']
        : [];
  }

  onRowSelect(index: number, row: any) {
    if (!this.isSelectable) return;

    if (this.selectedRows.has(index)) {
      this.selectedRows.delete(index);
      this.selectedItems = this.selectedItems.filter(item => item !== row);
    } else {
      if (this.selectedRows.size >= this.maxSelectable) {
        if (this.maxSelectable === 1) {
          this.selectedRows.clear();
          this.selectedItems = [];
        } else {
          return;
        }
      }
      this.selectedRows.add(index);
      this.selectedItems.push(row);
    }

    // Update formly model with processed selection
    const processedSelection = this.processSelectionOutput(
      this.maxSelectable === 1 ? this.selectedItems[0] : this.selectedItems
    );
    this.formControl.setValue(processedSelection);
  }

  private processSelectionOutput(selection: any | any[]): any {
    if (!selection) return null;

    if (Array.isArray(selection)) {
      return selection.map(item =>
        this.selectionOutputPath
          ? getValueFromNestedObject(item, this.selectionOutputPath)
          : item
      );
    }

    return this.selectionOutputPath
      ? getValueFromNestedObject(selection, this.selectionOutputPath)
      : selection;
  }

  // Modify the cell value rendering in the component
  getCellValue(row: any, column: TableColumn): any {
    const value = getValueFromNestedObject(row, column.key);
    return value ?? '';
  }

  isRowSelected(index: number): boolean {
    return this.selectedRows.has(index);
  }

  private loadData() {
    if (this.dataType === 'STATIC') {
      this.loadStaticData();
    } else {
      this.getDataSet();
    }
  }

  private loadStaticData() {
    this.loading = false;
    this.tableData = Array.isArray(this.staticRows) ? this.staticRows : [];
    this.totalItems = this.tableData.length;
    this.cd.detectChanges();
  }

  private setupSearchSubscription() {
    this.subscriptions.add(
      this.searchSubject
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(searchTerm => {
          if (this.dataType === 'STATIC') {
            this.filterStaticData(searchTerm);
            return;
          }

          const dataset = this.field.props?.['dataset'];
          if (!dataset?.['searching']) return;

          const params = { ...dataset['params'] };
          if (dataset['searchField']) {
            params[dataset['searchField']] = searchTerm;
          }

          this.currentPage = 1;
          this.getDataSet(params);
        })
    );
  }

  private filterStaticData(searchTerm: string) {
    const staticData = Array.isArray(this.staticRows) ? this.staticRows : [];
    if (!searchTerm || searchTerm.trim() === '') {
      this.tableData = staticData;
    } else {
      const term = searchTerm.toLowerCase().trim();
      this.tableData = staticData.filter(row => {
        return this.columns.some(col => {
          const value = this.getCellValue(row, col);
          return value?.toString().toLowerCase().includes(term);
        });
      });
    }
    this.totalItems = this.tableData.length;
    this.currentPage = 1;
    this.cd.detectChanges();
  }

  getDataSet(searchParams?: Record<string, any>) {
    if (this.loading) {
      return;
    }

    if (this.field.props?.['options']) {
      this.tableData = [];
      this.loading = false;
      return;
    }

    const dataset = this.field.props?.['dataset'];
    if (!dataset) {
      console.error('Dataset configuration is required');
      this.tableData = [];
      this.loading = false;
      this.cd.detectChanges();
      return;
    }

    if (!dataset['url']) {
      console.error('Invalid dataset property: url is required');
      this.tableData = [];
      this.loading = false;
      this.cd.detectChanges();
      return;
    }

    let url = dataset['url'];
    if (this.field.props?.['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const params = {
      ...dataset['params'],
      ...searchParams,
      page: this.currentPage - 1,
      size: this.itemsPerPage,
    };

    if (this.selectedSort?.key) {
      params['sort'] = this.selectedSort.key;
    }

    const fieldSubType = this.field.props?.['subType'];
    if (fieldSubType && this.dataFetchFields.includes(fieldSubType)) {
      this.getDataByIntegrationFetchSync(
        this.field.props?.['code'],
        url,
        dataset['dataField']
      );
    } else {
      const httpMethod = dataset['httpMethod'];
      if (httpMethod === 'POST') {
        this.getDataByPostMethod(
          url,
          dataset['dataField'],
          params,
          dataset['headers']
        );
      } else {
        this.getDataByGetMethod(
          url,
          dataset['dataField'],
          params,
          dataset['headers']
        );
      }
    }
  }

  getDataByPostMethod(
    url: string,
    dataField: string,
    params: Record<string, any> = {},
    headers: Record<string, string> = {}
  ) {
    this.loading = true;

    // Remove undefined and null values from params
    const cleanParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    this.subscriptions.add(
      this.http.post<any>(url, cleanParams, { headers }).subscribe({
        next: res => {
          this.processResponse(res, dataField);
        },
        error: error => {
          console.error('Error fetching data:', error);
          this.loading = false;
          this.cd.detectChanges();
        },
      })
    );
  }

  getDataByGetMethod(
    url: string,
    dataField: string,
    params: Record<string, any> = {},
    headers: Record<string, string> = {}
  ) {
    // Remove undefined and null values from params
    type ParamsType = Record<string, string | number | boolean>;

    const cleanParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce<ParamsType>(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value,
        }),
        {} as ParamsType
      );

    const queryParams = Object.keys(cleanParams)
      .map(key => `${key}=${encodeURIComponent(String(cleanParams[key]))}`)
      .join('&');

    this.loading = true;
    const finalUrl = queryParams ? `${url}?${queryParams}` : url;

    this.subscriptions.add(
      this.http.get<any>(finalUrl, { headers }).subscribe({
        next: res => {
          this.processResponse(res, dataField);
        },
        error: error => {
          console.error('Error fetching data:', error);
          this.loading = false;
          this.cd.detectChanges();
        },
      })
    );
  }

  getDataByIntegrationFetchSync(
    endpointCode: string,
    url: string,
    dataField: string
  ): void {
    this.loading = true;
    const req: IIntegrationFetchSyncRequest = {
      callerId: alphaNanoIdUtil(50),
      endpointCode: endpointCode,
      requester: alphaNanoIdUtil(50),
      payload: {
        ServiceName: endpointCode,
        page: this.currentPage - 1,
        size: this.itemsPerPage,
        ...(this.selectedSort?.key && { sort: this.selectedSort.key }),
      },
    };

    this.subscriptions.add(
      this.http.post<IIntegrationFetchSyncRequestResponse>(url, req).subscribe({
        next: res => {
          if (res.status) {
            const responseObject = JSON.parse(res.data.response);
            if (!dataField) {
              this.processTableData(responseObject);
            } else {
              const data = getValueFromNestedObject(responseObject, dataField);
              this.processTableData(data);
            }
          }
          this.loading = false;
          this.cd.detectChanges();
        },
        error: error => {
          console.error('Error fetching data:', error);
          this.loading = false;
          this.cd.detectChanges();
        },
      })
    );
  }

  private processResponse(res: any, dataField: string) {
    // First get the dataset items like in dropdown
    const datasetItems = Array.isArray(res)
      ? res
      : getValueFromNestedObject(res, dataField);

    if (!Array.isArray(datasetItems)) {
      this.tableData = [];
      this.totalItems = 0;
      return;
    }

    // Process each item in the dataset
    const bindLabel: string = this.field.props?.['bindLabel'] ?? 'label';
    this.tableData = datasetItems.map(item => {
      if (!item[bindLabel]) {
        item[bindLabel] = this.translateService.instant(
          <string>item[bindLabel]
        );
      }
      // translate details object
      if (item.details && typeof item.details === 'object') {
        const translatedDetails = Object.entries(item.details).reduce(
          (acc, [key, value]) => {
            const translatedValue =
              typeof value === 'string'
                ? this.translateService.instant(<string>value)
                : value;
            return {
              ...acc,
              details: {
                ...acc.details,
                [key]: translatedValue,
              },
            };
          },
          { ...item, details: {} }
        );
        return translatedDetails;
      }

      return item;
    });

    this.totalItems = this.tableData.length;
    this.loading = false;
    this.cd.detectChanges();
  }

  private processTableData(data: any) {
    let processedData;

    if (Array.isArray(data)) {
      processedData = data;
    } else if (data?.content) {
      processedData = data.content;
    } else if (typeof data === 'object') {
      processedData = [data];
    } else {
      processedData = [];
    }

    this.tableData = processedData;
    this.totalItems = Array.isArray(data)
      ? data.length
      : data?.totalElements || processedData.length;

    // Clear selections
    this.selectedRows.clear();
    this.selectedItems = [];
    this.formControl.setValue(this.maxSelectable === 1 ? null : []);
  }

  onPageChange(event: any) {
    this.currentPage = event.page;
    this.itemsPerPage = event.pageSize;

    if (this.dataType === 'STATIC') {
      // For static data, pagination is handled by the UI component
      this.cd.detectChanges();
    } else {
      this.getDataSet();
    }
  }

  onSortChange(sortKey: string) {
    this.selectedSort = this.sortOptions.find(opt => opt.key === sortKey);

    if (this.dataType === 'STATIC') {
      this.sortStaticData(sortKey);
    } else {
      this.getDataSet();
    }
  }

  private sortStaticData(sortKey: string) {
    if (!sortKey || !this.tableData.length) return;

    this.tableData.sort((a, b) => {
      const aValue = this.getCellValue(a, { key: sortKey } as TableColumn);
      const bValue = this.getCellValue(b, { key: sortKey } as TableColumn);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue);
      }

      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });

    this.cd.detectChanges();
  }

  onSearch(searchTerm: string) {
    this.searchSubject.next(searchTerm);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
    this.subscriptions.unsubscribe();
  }
}
